import React, { useState } from 'react';
import { COLORS } from '../../../../core/constants/colors';
import { useAdminOverride } from '../hooks/useAdminOverride';
import { AdminCompany } from '../types';
import { DemoVideoEntry, DemoVideoCreatePayload } from '../types/override.types';
import { FieldOverrideReview } from '../../../companies/components/FieldOverrideReview';

interface Props {
  companyId: number;
  company: AdminCompany;
  onFieldsUpdated: () => void;
  linkedInData?: { extracted_data?: Record<string, any> } | null;
  webEnrichmentData?: { extracted_data?: Record<string, any> } | null;
}

export const AdminOverrideTab: React.FC<Props> = ({
  companyId,
  company,
  onFieldsUpdated,
  linkedInData,
  webEnrichmentData,
}) => {
  const {
    videos,
    isLoading,
    isSubmitting,
    error,
    createVideo,
    updateVideo,
    deleteVideo,
  } = useAdminOverride(companyId);

  // ── Video form state ──
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState<DemoVideoEntry | null>(null);
  const [confirmDeleteVideoId, setConfirmDeleteVideoId] = useState<string | null>(null);
  const [videoFormError, setVideoFormError] = useState<string | null>(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoThumbnail, setVideoThumbnail] = useState('');
  const [videoDescription, setVideoDescription] = useState('');
  const [videoDuration, setVideoDuration] = useState('');
  const [videoOrder, setVideoOrder] = useState(0);

  const resetVideoForm = () => {
    setVideoTitle('');
    setVideoUrl('');
    setVideoThumbnail('');
    setVideoDescription('');
    setVideoDuration('');
    setVideoOrder(0);
    setVideoFormError(null);
  };

  const openAddVideo = () => {
    resetVideoForm();
    setEditingVideo(null);
    setShowVideoForm(true);
  };

  const openEditVideo = (v: DemoVideoEntry) => {
    setVideoTitle(v.title);
    setVideoUrl(v.video_url);
    setVideoThumbnail(v.thumbnail_url || '');
    setVideoDescription(v.description || '');
    setVideoDuration(v.duration_seconds != null ? String(v.duration_seconds) : '');
    setVideoOrder(v.order_position);
    setVideoFormError(null);
    setEditingVideo(v);
    setShowVideoForm(true);
  };

  const closeVideoForm = () => {
    setShowVideoForm(false);
    setEditingVideo(null);
    resetVideoForm();
  };

  const handleVideoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoTitle.trim() || !videoUrl.trim()) {
      setVideoFormError('Title and video URL are required');
      return;
    }
    setVideoFormError(null);

    const payload: DemoVideoCreatePayload = {
      title: videoTitle.trim(),
      video_url: videoUrl.trim(),
      thumbnail_url: videoThumbnail.trim() || undefined,
      description: videoDescription.trim() || undefined,
      duration_seconds: videoDuration ? parseInt(videoDuration) : undefined,
      order_position: videoOrder,
    };

    try {
      if (editingVideo) {
        await updateVideo(editingVideo.id, payload);
      } else {
        await createVideo(payload);
      }
      closeVideoForm();
    } catch (err: any) {
      setVideoFormError(err.message || 'Operation failed');
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      await deleteVideo(videoId);
      setConfirmDeleteVideoId(null);
    } catch (err: any) {
      setVideoFormError(err.message || 'Delete failed');
    }
  };

  return (
    <div>
      {/* ── Section 1: Field Overrides (shared component) ── */}
      <div className="mb-8">
        <FieldOverrideReview
          companyId={companyId}
          company={company}
          onFieldsUpdated={onFieldsUpdated}
          linkedInData={linkedInData}
          webEnrichmentData={webEnrichmentData}
          accentColor={COLORS.admin.primary}
        />
      </div>

      {/* ── Section 2: Demo Videos (admin-only) ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Demo Videos</h3>
          <button
            onClick={openAddVideo}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
            style={{ backgroundColor: COLORS.admin.primary }}
          >
            + Add Video
          </button>
        </div>

        {error && (
          <p className="text-sm mb-3" style={{ color: '#EF4444' }}>
            {error}
          </p>
        )}
        {videoFormError && (
          <p className="text-sm mb-3" style={{ color: '#EF4444' }}>
            {videoFormError}
          </p>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div
              className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
            />
          </div>
        ) : videos.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center">No demo videos yet.</p>
        ) : (
          <div className="space-y-3">
            {videos.map((video) => (
              <div
                key={video.id}
                className="flex items-start gap-4 p-4 rounded-lg border"
                style={{ borderColor: '#E5E7EB' }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{video.title}</p>
                  <a
                    href={video.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs break-all"
                    style={{ color: COLORS.admin.primary }}
                  >
                    {video.video_url}
                  </a>
                  {video.description && (
                    <p className="text-xs text-gray-500 mt-1">{video.description}</p>
                  )}
                  {video.duration_seconds != null && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Duration: {Math.floor(video.duration_seconds / 60)}m{' '}
                      {video.duration_seconds % 60}s
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => openEditVideo(video)}
                    disabled={isSubmitting}
                    className="px-3 py-1 text-xs font-medium rounded border text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    style={{ borderColor: '#E5E7EB' }}
                  >
                    Edit
                  </button>
                  {confirmDeleteVideoId === video.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDeleteVideo(video.id)}
                        disabled={isSubmitting}
                        className="px-2 py-1 text-xs font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setConfirmDeleteVideoId(null)}
                        className="px-2 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteVideoId(video.id)}
                      disabled={isSubmitting}
                      className="px-3 py-1 text-xs font-medium rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Video Add / Edit Modal */}
      {showVideoForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={closeVideoForm} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <h4 className="text-base font-bold text-gray-900 mb-4">
              {editingVideo ? 'Edit Demo Video' : 'Add Demo Video'}
            </h4>

            <form onSubmit={handleVideoSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Product Demo"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Video URL *
                </label>
                <input
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Thumbnail URL
                </label>
                <input
                  type="url"
                  value={videoThumbnail}
                  onChange={(e) => setVideoThumbnail(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={videoDescription}
                  onChange={(e) => setVideoDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Duration (seconds)
                  </label>
                  <input
                    type="number"
                    value={videoDuration}
                    onChange={(e) => setVideoDuration(e.target.value)}
                    min={0}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={videoOrder}
                    onChange={(e) => setVideoOrder(parseInt(e.target.value) || 0)}
                    min={0}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
              </div>

              {videoFormError && (
                <p className="text-sm" style={{ color: '#EF4444' }}>
                  {videoFormError}
                </p>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeVideoForm}
                  className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50"
                  style={{ backgroundColor: COLORS.admin.primary }}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
